import React, { useEffect, useState } from 'react';
import { useDashboardDomainContext } from "../../DashboardDomainProvider";
import { IconControl } from 'spark-core-dx/components';

const TabWrapper = ({ handleContextMenu, scrollRef, onAddPage, showAddPageButton }) => {
  const {
    visiblePages,
    selectedPageData,
    navigateToPage,
    isViewMode,
    reorderPage,
  } = useDashboardDomainContext();

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [draggingPageKey, setDraggingPageKey] = useState(null);
  const [dropTargetPageKey, setDropTargetPageKey] = useState(null);

  useEffect(() => {
    const checkOverflow = () => {
      const el = scrollRef.current;
      if (el) {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [scrollRef, visiblePages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      const atStart = el.scrollLeft === 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setIsAtStart(atStart);
      setIsAtEnd(atEnd);
    };

    window.addEventListener('resize', handleScroll);
    el.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('resize', handleScroll);
      el.removeEventListener('scroll', handleScroll);
    };
  }, [scrollRef, visiblePages]);

  const scrollByOneTab = (direction) => {
    const container = scrollRef.current;
    if (!container) return;

    const tabs = Array.from(container.querySelectorAll(".tab"));
    const containerRect = container.getBoundingClientRect();

    if (direction === 'right') {
      const nextTab = tabs.find((tab) => tab.getBoundingClientRect().left > containerRect.left + 10);
      if (nextTab) {
        container.scrollTo({ left: nextTab.offsetLeft - 85, behavior: 'smooth' });
      }
    } else {
      const previousTabs = tabs.filter((tab) => tab.getBoundingClientRect().left < containerRect.left - 10);
      const prevTab = previousTabs[previousTabs.length - 1];
      if (prevTab) {
        container.scrollTo({ left: Math.max(prevTab.offsetLeft - 85, 0), behavior: 'smooth' });
      }
    }
  };

  const canDragPages = !isViewMode;

  const handleDragStart = (event, pageKey) => {
    if (!canDragPages) {
      return;
    }

    setDraggingPageKey(pageKey);
    setDropTargetPageKey(pageKey);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', pageKey);
  };

  const handleDragOver = (event, pageKey) => {
    if (!canDragPages || !draggingPageKey || draggingPageKey === pageKey) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dropTargetPageKey !== pageKey) {
      setDropTargetPageKey(pageKey);
    }
  };

  const handleDragEnd = () => {
    setDraggingPageKey(null);
    setDropTargetPageKey(null);
  };

  const handleDrop = async (event, pageKey) => {
    if (!canDragPages) {
      return;
    }

    event.preventDefault();
    const sourcePageKey = draggingPageKey || event.dataTransfer.getData('text/plain');
    if (!sourcePageKey || sourcePageKey === pageKey) {
      handleDragEnd();
      return;
    }

    await reorderPage(sourcePageKey, pageKey);
    handleDragEnd();
  };

  return (
    <div className="tab-wrapper">
      {showAddPageButton && (
        <div className="tab-add-page">
          <IconControl className="menu-button-icon large" iconKey={"add"} onClick={onAddPage} />
        </div>
      )}

      <div className="tab-container" ref={scrollRef}>
        {visiblePages.map((page) => (
          <div
            className={`tab${selectedPageData?.Key === page?.Key ? ' selected' : ''}${page?.IsDeleted ? ' deleted' : ''}${draggingPageKey === page?.Key ? ' dragging' : ''}${dropTargetPageKey === page?.Key && draggingPageKey !== page?.Key ? ' drag-over' : ''}`}
            key={page?.Key}
            onClick={() => navigateToPage(page?.Key)}
            title={page?.Name || "Untitled Page"}
            draggable={canDragPages}
            onDragStart={(event) => handleDragStart(event, page?.Key)}
            onDragOver={(event) => handleDragOver(event, page?.Key)}
            onDragEnd={handleDragEnd}
            onDrop={(event) => handleDrop(event, page?.Key)}
          >
            <div className="tab-name">
              {page?.Icon && <IconControl iconKey={page.Icon} />}
              <div className="tab-text">{page?.Name || "Untitled Page"}</div>
            </div>
            {!isViewMode && (
              <IconControl
                onClick={(e) => { handleContextMenu(e, page?.Key); e.stopPropagation(); }}
                iconKey={"arrowdropdown"}
              />
            )}
          </div>
        ))}
      </div>

      <div className="menu-buttons right">
        <IconControl
          className={`menu-button-icon left-arrow${!isOverflowing || isAtStart ? ' disabled' : ''}`}
          iconKey={"arrowdropdown"}
          onClick={() => scrollByOneTab('left')}
        />
        <IconControl
          className={`menu-button-icon right-arrow${!isOverflowing || isAtEnd ? ' disabled' : ''}`}
          iconKey={"arrowdropdown"}
          onClick={() => scrollByOneTab('right')}
        />
      </div>
    </div>
  );
};

export default TabWrapper;
